import { Injectable } from '@angular/core';

/**
 * Interfaccia per i parametri di ricerca condivisibili
 */
export interface SearchParams {
  filterText?: string;
  bimOnly?: boolean;
  serviziAccessoriOnly?: boolean;
  selectedTagsByCategory?: Record<string, string[]>;
  selectedClienti?: string[];
  selectedProjectManagers?: string[];
  selectedCategorieServizio?: string[];
  selectedCommessa?: string; // Singolo codice commessa (es: "C001")
  selectedCertificato?: string; // Singolo codice certificato
}

/**
 * Servizio per gestire la condivisione dei risultati di ricerca tramite URL
 */
@Injectable({
  providedIn: 'root'
})
export class SearchShareService {

  /**
   * Serializza i parametri di ricerca in una query string
   */
  serializeSearchParams(params: SearchParams): string {
    const queryParams: string[] = [];

    // Testo di ricerca
    if (params.filterText && params.filterText.trim()) {
      queryParams.push(`q=${encodeURIComponent(params.filterText.trim())}`);
    }

    // Filtro BIM
    if (params.bimOnly) {
      queryParams.push('bim=true');
    }

    // Filtro servizi accessori
    if (params.serviziAccessoriOnly) {
      queryParams.push('sa=true');
    }

    // Cliente (multi)
    if (params.selectedClienti && params.selectedClienti.length > 0) {
      const encoded = params.selectedClienti.map(v => encodeURIComponent(v)).join(',');
      queryParams.push(`cli=${encoded}`);
    }

    // Project Manager (multi)
    if (params.selectedProjectManagers && params.selectedProjectManagers.length > 0) {
      const encoded = params.selectedProjectManagers.map(v => encodeURIComponent(v)).join(',');
      queryParams.push(`pm=${encoded}`);
    }

    // Categorie servizio (multi)
    if (params.selectedCategorieServizio && params.selectedCategorieServizio.length > 0) {
      const encoded = params.selectedCategorieServizio.map(v => encodeURIComponent(v)).join(',');
      queryParams.push(`cs=${encoded}`);
    }

    // Commessa (singolo codice)
    if (params.selectedCommessa) {
      queryParams.push(`comm=${encodeURIComponent(params.selectedCommessa)}`);
    }

    // Certificato (singolo codice)
    if (params.selectedCertificato) {
      queryParams.push(`cert=${encodeURIComponent(params.selectedCertificato)}`);
    }

    // Tag selezionati per categoria
    if (params.selectedTagsByCategory) {
      const activeCategories = Object.keys(params.selectedTagsByCategory).filter(
        cat => params.selectedTagsByCategory![cat] && params.selectedTagsByCategory![cat].length > 0
      );

      if (activeCategories.length > 0) {
        // Formato: tags=CATEGORIA1:tag1,tag2|CATEGORIA2:tag3
        const tagsParts: string[] = [];
        activeCategories.forEach(cat => {
          const tags = params.selectedTagsByCategory![cat];
          if (tags && tags.length > 0) {
            const encodedTags = tags.map(t => encodeURIComponent(t)).join(',');
            tagsParts.push(`${encodeURIComponent(cat)}:${encodedTags}`);
          }
        });
        if (tagsParts.length > 0) {
          queryParams.push(`tags=${tagsParts.join('|')}`);
        }
      }
    }

    return queryParams.join('&');
  }

  /**
   * Deserializza i parametri di ricerca da una query string
   */
  deserializeSearchParams(queryString: string): SearchParams {
    const params: SearchParams = {};
    const urlParams = new URLSearchParams(queryString);

    // Testo di ricerca
    const q = urlParams.get('q');
    if (q) {
      params.filterText = decodeURIComponent(q);
    }

    // Filtro BIM
    const bim = urlParams.get('bim');
    if (bim === 'true') {
      params.bimOnly = true;
    }

    // Filtro servizi accessori
    const sa = urlParams.get('sa');
    if (sa === 'true') {
      params.serviziAccessoriOnly = true;
    }

    // Cliente (multi)
    const cli = urlParams.get('cli');
    if (cli) {
      params.selectedClienti = cli
        .split(',')
        .map(v => decodeURIComponent(v))
        .filter(v => v && v.trim().length > 0);
    }

    // Project Manager (multi)
    const pm = urlParams.get('pm');
    if (pm) {
      params.selectedProjectManagers = pm
        .split(',')
        .map(v => decodeURIComponent(v))
        .filter(v => v && v.trim().length > 0);
    }

    // Categorie servizio (multi)
    const cs = urlParams.get('cs');
    if (cs) {
      params.selectedCategorieServizio = cs
        .split(',')
        .map(v => decodeURIComponent(v))
        .filter(v => v && v.trim().length > 0);
    }

    // Commessa (singolo codice)
    const comm = urlParams.get('comm');
    if (comm) {
      params.selectedCommessa = decodeURIComponent(comm);
    }

    // Certificato (singolo codice)
    const cert = urlParams.get('cert');
    if (cert) {
      params.selectedCertificato = decodeURIComponent(cert);
    }

    // Tag selezionati per categoria
    const tagsParam = urlParams.get('tags');
    if (tagsParam) {
      params.selectedTagsByCategory = {};
      // Formato: CATEGORIA1:tag1,tag2|CATEGORIA2:tag3
      const categoryParts = tagsParam.split('|');
      categoryParts.forEach(part => {
        const [category, tagsStr] = part.split(':');
        if (category && tagsStr) {
          const decodedCategory = decodeURIComponent(category);
          const tags = tagsStr.split(',').map(t => decodeURIComponent(t));
          params.selectedTagsByCategory![decodedCategory] = tags;
        }
      });
    }

    return params;
  }

  /**
   * Genera un URL completo con i parametri di ricerca
   * Assicura che l'URL punti sempre alla root (/), rimuovendo eventuali pathname
   */
  generateShareUrl(baseUrl: string, params: SearchParams): string {
    // Normalizza l'URL base per assicurarsi che punti sempre alla root
    let normalizedBaseUrl = baseUrl;
    try {
      const url = new URL(baseUrl);
      // Forza il pathname a '/' per assicurarsi che punti sempre alla home
      url.pathname = '/';
      normalizedBaseUrl = url.toString();
    } catch (e) {
      // Se baseUrl non è un URL valido, usa solo l'origin con /
      if (baseUrl.includes('://')) {
        const origin = baseUrl.split('/').slice(0, 3).join('/');
        normalizedBaseUrl = origin + '/';
      } else {
        normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
      }
    }
    
    const queryString = this.serializeSearchParams(params);
    if (queryString) {
      return `${normalizedBaseUrl}?${queryString}`;
    }
    return normalizedBaseUrl;
  }

  /**
   * Verifica se ci sono parametri di ricerca attivi
   */
  hasActiveParams(params: SearchParams): boolean {
    if (params.filterText && params.filterText.trim()) {
      return true;
    }
    if (params.bimOnly) {
      return true;
    }
    if (params.serviziAccessoriOnly) {
      return true;
    }
    if (params.selectedClienti && params.selectedClienti.length > 0) {
      return true;
    }
    if (params.selectedProjectManagers && params.selectedProjectManagers.length > 0) {
      return true;
    }
    if (params.selectedCategorieServizio && params.selectedCategorieServizio.length > 0) {
      return true;
    }
    if (params.selectedCommessa && params.selectedCommessa.trim()) {
      return true;
    }
    if (params.selectedCertificato && params.selectedCertificato.trim()) {
      return true;
    }
    if (params.selectedTagsByCategory) {
      const hasActiveTags = Object.keys(params.selectedTagsByCategory).some(
        cat => params.selectedTagsByCategory![cat] && params.selectedTagsByCategory![cat].length > 0
      );
      if (hasActiveTags) {
        return true;
      }
    }
    return false;
  }
}

